import { type Cluster, ClusterScalabilityStatus } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { type StatsigClient } from '@statsig/react-bindings';
import { type QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { memoize } from 'micro-memoize';
import { type ListGlobalCloudProvidersDto, platformService } from '@/api/services/platform';
import { isFreeTierCloudProviderAvailable } from '@/components/Clusters/ClusterSetup/helpers/isFreeTierCloudProviderAvailable';
import { CLOUD_PROVIDER_MAP } from '@/components/Clusters/constants';
import {
  type FreeTierExperimentCloudProvider,
  getAvailableFreeTierProviders,
  getRandomCloudProvider,
  selectDefaultRegion,
  selectFreeTierProvider,
} from '@/components/Clusters/helpers';
import { checkFeatureGateClusterCreation } from '@/helpers/statsig/featureGate';
import { enqueueSnackbar } from '@/hooks/use-qdrant-snackbar';
import { clusterIdUrlParam } from '@/routes/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/route.hooks';
import { type AccountId, accountIdUrlParam } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { type ProviderRegionFilter, type ProviderRegionFilterArg } from '@/utils/cloud-provider-region';
import { type ClusterCreateBaseSearchParams, type ClusterCreateSearchParams } from '../utils';

function matchesExperimentCloudProvider(
  id: string | undefined,
  experimentCloudProvider: FreeTierExperimentCloudProvider,
) {
  if (!id) {
    // No concrete provider to match against experiment; reject.
    return false;
  }

  if (experimentCloudProvider === '') {
    // Empty string means "no experiment restriction" — accept any defined provider id.
    return true;
  }

  return experimentCloudProvider === id;
}

/**
 * Filter used by create route provider/region normalization.
 * - For provider entries, if template is free: enforce available + freeTier and experiment restriction.
 * - For region entries, keep existing behavior (available only).
 */
export const createTemplateFilterByExperiment = memoize(function createTemplateFilterByExperimentMemoized(
  template: 'free' | 'standard',
  experimentCloudProvider: FreeTierExperimentCloudProvider,
) {
  return ({ id, available, freeTier, provider }: ProviderRegionFilterArg) => {
    if (provider === undefined && template === 'free') {
      return available && freeTier && matchesExperimentCloudProvider(id, experimentCloudProvider);
    }
    return available;
  };
});

/**
 * Checks whether the given provider id is currently available and supports free tier.
 *
 * @param cloudProviders - Full list of cloud provider descriptors returned from the platform API.
 * @param providerId - Provider id to validate.
 * @returns `true` when a matching provider exists with `available=true` and `freeTier=true`; otherwise `false`.
 */
function isValidFreeTierProvider(cloudProviders: ListGlobalCloudProvidersDto, providerId: string) {
  return cloudProviders.some((item) => item.id === providerId && item.available && item.freeTier);
}

export async function resolveValidProviderRegionPair<
  T extends
    | Pick<ClusterCreateBaseSearchParams, 'provider' | 'region'>
    | Pick<ClusterCreateSearchParams, 'provider' | 'region'>,
>({
  queryClient,
  search,
  accountId,
  listCloudProvidersQuery,
  filter = ({ available }) => available,
}: {
  queryClient: QueryClient;
  search: T;
  accountId: AccountId;
  listCloudProvidersQuery: ReturnType<typeof platformService.listCloudProvidersQuery>;
  filter?: ProviderRegionFilter;
}) {
  const { items: cloudProviders } = await queryClient.fetchQuery(listCloudProvidersQuery);
  if (!search.provider) {
    // We don't want to select Hybrid cloud provider by default, so we filter it out
    const noHybridCloudProviders = cloudProviders.filter(
      (item) => item.available && item.id !== CLOUD_PROVIDER_MAP.HYBRID,
    );
    const provider = getRandomCloudProvider(noHybridCloudProviders.filter(filter));
    const { items: cloudProviderRegions } = await queryClient.fetchQuery(
      platformService.listCloudProviderRegionsQuery({ accountId, cloudProviderId: provider }),
    );
    const region = selectDefaultRegion({
      cloudProviderRegions: cloudProviderRegions.filter((r) => filter({ ...r, provider })),
      preSelectedRegion: search.region,
    });

    throw redirect({
      to: '.',
      params: { accountId },
      search: { ...search, provider, region },
      replace: true,
    });
  }

  const { items: cloudProviderRegions } = await queryClient.fetchQuery(
    platformService.listCloudProviderRegionsQuery({ accountId, cloudProviderId: search.provider }),
  );
  const region = selectDefaultRegion({
    cloudProviderRegions: cloudProviderRegions.filter((r) => filter({ ...r, provider: search.provider })),
    preSelectedRegion: search.region,
  });

  if (search.region !== region) {
    throw redirect({
      to: '.',
      params: { accountId },
      search: { ...search, region },
      replace: true,
    });
  }

  return { provider: search.provider, region } as Required<T>;
}

export async function resolveValidFreeTierProvider({
  queryClient,
  listCloudProvidersQuery,
  search,
  experimentCloudProvider = '',
}: {
  queryClient: QueryClient;
  listCloudProvidersQuery: ReturnType<typeof platformService.listCloudProvidersQuery>;
  search: Pick<ClusterCreateSearchParams, 'provider' | 'template'>;
  experimentCloudProvider?: FreeTierExperimentCloudProvider;
}) {
  if (search.template !== 'free') {
    return;
  }

  const { items: cloudProviders } = await queryClient.fetchQuery(listCloudProvidersQuery);
  // check if no cloud providers at all offer free tier for this account
  if (!isFreeTierCloudProviderAvailable(cloudProviders)) {
    throw redirect({
      to: '.',
      search: { ...search, template: 'standard' },
      replace: true,
    });
  }

  // If provider is missing (another resolver will set it) or already valid for free tier, keep current flow.
  if (search.provider === undefined || isValidFreeTierProvider(cloudProviders, search.provider)) {
    return;
  }

  const freeProviders = getAvailableFreeTierProviders(cloudProviders);
  const provider = selectFreeTierProvider(freeProviders, experimentCloudProvider);

  throw redirect({
    to: '.',
    search: { ...search, provider },
    replace: true,
  });
}

export async function resolvePreloadedProvidersRegions({
  queryClient,
  accountId,
  filter = ({ available }) => available,
}: {
  queryClient: QueryClient;
  accountId?: string;
  filter?: (item: { id: string; available: boolean; freeTier: boolean }) => boolean;
}) {
  let cloudProviders: ListGlobalCloudProvidersDto = [];

  if (accountId) {
    const { items } = await queryClient.fetchQuery(platformService.listCloudProvidersQuery({ accountId }));
    cloudProviders = items;
  } else {
    const { items } = await queryClient.fetchQuery(platformService.listGlobalCloudProvidersQuery());
    cloudProviders = items;
  }

  return Promise.all(
    cloudProviders.filter(filter).map(({ id: cloudProviderId }) => {
      if (accountId) {
        return queryClient.fetchQuery(platformService.listCloudProviderRegionsQuery({ accountId, cloudProviderId }));
      }
      return queryClient.fetchQuery(platformService.listGlobalCloudProviderRegionsQuery({ cloudProviderId }));
    }),
  );
}

export function resolveClusterCreationEnabled(statsigClient: StatsigClient, params: { accountId: AccountId }) {
  // Reading feature flags at route level require the user-context to be ensured first (done at parent route)
  const clusterCreationEnabled = checkFeatureGateClusterCreation(statsigClient);

  if (!clusterCreationEnabled) {
    enqueueSnackbar({ message: 'Cluster creation is currently disabled', variant: 'warning' });

    throw redirect({ to: '/accounts/$accountId/clusters', params });
  }
}

export function ensureClusterIsScalable(cluster: Cluster): asserts cluster is Cluster {
  const scalabilityInfo = cluster.state?.capabilities?.scalabilityInfo;
  if (scalabilityInfo?.status !== ClusterScalabilityStatus.SCALABLE) {
    const message = scalabilityInfo?.reason ?? 'An error occurred while checking if the cluster is scalable';
    enqueueSnackbar({ message, variant: 'warning' });

    throw redirect({
      to: '/accounts/$accountId/clusters/$clusterId',
      params: {
        accountId: accountIdUrlParam.parse(cluster.accountId),
        clusterId: clusterIdUrlParam.parse(cluster.id),
      },
    });
  }
}
